import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import s from './ui.module.css';

export interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string | undefined;
  /** Draws a divider above this item. */
  separated?: boolean;
}

/** A small dropdown of further actions under a button ("更多"). Closes on pick, Escape or an outside click. */
export function Menu({ label, items }: { label: ReactNode; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <div className={s.menu} ref={ref}>
      <Button aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {label} ▾
      </Button>
      {open && (
        <div className={s.menuList} role="menu">
          {items.map((it) => (
            <div key={it.label} style={{ display: 'contents' }}>
              {it.separated && <div className={s.menuSep} />}
              <button
                type="button"
                role="menuitem"
                data-danger={it.danger || undefined}
                disabled={it.disabled}
                title={it.title}
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
              >
                {it.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Page tabs; the caller keeps the value (often in the URL). */
export function Tabs<T extends string>({ value, tabs, onChange, label }: { value: T; tabs: ReadonlyArray<{ value: T; label: ReactNode }>; onChange: (v: T) => void; label: string }) {
  return (
    <div className={s.tabs} role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button key={t.value} type="button" role="tab" aria-selected={t.value === value} onClick={() => onChange(t.value)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}
